<template>
  <!-- Comments plan 3b: one input for new comments and replies: text with @mentions and files. -->
  <div class="composer" @dragover.prevent @drop.prevent="dropped">
    <div class="composer-field">
      <textarea
        ref="input" v-model="text" :data-test="testId" class="comments-input" :rows="rows" :placeholder="placeholder"
        @input="typed" @keydown="keyed" @click="typed"
      ></textarea>
      <ul v-if="matches.length" class="composer-mentions" data-test="mention-list" role="listbox">
        <li
          v-for="(m, i) in matches" :key="m.user_id" role="option" :aria-selected="i === active"
          :class="{ active: i === active }" data-test="mention-option" @mousedown.prevent="pick(m)"
        >{{ m.full_name }}</li>
      </ul>
    </div>
    <ul v-if="files.length" class="composer-files">
      <li v-for="f in files" :key="f.key" class="composer-file" data-test="composer-file">
        <span class="composer-file-name">{{ f.name }}</span>
        <span v-if="f.state === 'up'" class="composer-file-state">…</span>
        <span v-else-if="f.state === 'error'" class="composer-file-state error">!</span>
        <button type="button" class="comments-link" :aria-label="$t('webmag.comments.close')" @click="removeFile(f)">×</button>
      </li>
    </ul>
    <div class="composer-row">
      <label class="comments-link composer-attach" :title="$t('webmag.comments.attach')">
        <input type="file" multiple :accept="accept" data-test="composer-attach" @change="chosen" />
        📎 {{ $t('webmag.comments.attach') }}
      </label>
      <button
        type="button" class="comments-send" :data-test="`${testId}-send`" :disabled="!ready" @click="submit"
      >{{ sendLabel }}</button>
    </div>
  </div>
</template>

<script>
import CommentFileUpload from '@/graphQlQueries/mutations/commentFileUpload';
import { ACCEPT, checkFiles, typeFor } from '@/utils/commentFiles';
import {
  insertMention, matchMembers, mentionIds, mentionQuery,
} from '@/utils/mentions';

let fileKey = 0;

export default {
  name: 'CommentComposer',
  props: {
    pageId: { type: Number, required: true },
    members: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    sendLabel: { type: String, default: '' },
    testId: { type: String, default: 'comment-new' },
    rows: { type: Number, default: 2 },
    // An empty text is fine (a change request, or files only).
    allowEmpty: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data: () => ({
    text: '',
    files: [],
    picked: [],
    query: null,
    active: 0,
    accept: ACCEPT,
  }),
  computed: {
    matches() {
      return this.query === null ? [] : matchMembers(this.members, this.query);
    },
    uploading() {
      return this.files.some((f) => f.state === 'up');
    },
    ready() {
      const done = this.files.filter((f) => f.state === 'done');
      return !this.busy && !this.uploading && (!!this.text.trim() || done.length > 0 || this.allowEmpty);
    },
  },
  methods: {
    focus() {
      if (this.$refs.input) this.$refs.input.focus();
    },
    typed() {
      const el = this.$refs.input;
      this.query = el ? mentionQuery(this.text, el.selectionStart) : null;
      this.active = 0;
    },
    keyed(e) {
      if (!this.matches.length) return;
      if (e.key === 'ArrowDown') this.active = (this.active + 1) % this.matches.length;
      else if (e.key === 'ArrowUp') this.active = (this.active - 1 + this.matches.length) % this.matches.length;
      else if (e.key === 'Enter' || e.key === 'Tab') this.pick(this.matches[this.active]);
      else if (e.key === 'Escape') this.query = null;
      else return;
      e.preventDefault();
    },
    pick(member) {
      const el = this.$refs.input;
      const caret = el ? el.selectionStart : this.text.length;
      const out = insertMention(this.text, caret, member);
      this.text = out.text;
      if (!this.picked.some((p) => p.user_id === member.user_id)) this.picked.push(member);
      this.query = null;
      this.$nextTick(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(out.caret, out.caret);
        }
      });
    },
    chosen(e) {
      this.add(Array.from(e.target.files || []));
      e.target.value = '';
    },
    dropped(e) {
      this.add(Array.from((e.dataTransfer && e.dataTransfer.files) || []));
    },
    add(list) {
      const { ok, errors } = checkFiles(list, this.files.length);
      errors.forEach((err) => this.$bvToast.toast(this.$t(err.key, { name: err.name }), { variant: 'warning', solid: true }));
      ok.forEach((file) => this.upload(file));
    },
    async upload(file) {
      fileKey += 1;
      const entry = {
        key: fileKey, name: file.name, size: file.size, type: typeFor(file.name), url: '', state: 'up',
      };
      this.files.push(entry);
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CommentFileUpload,
          variables: { object: { pageId: this.pageId, fileName: file.name, size: file.size } },
        });
        const out = data && data.commentFileUpload;
        if (!out || !out.ok) throw new Error((out && out.error) || 'failed');
        const res = await fetch(out.upload, { method: 'PUT', headers: { 'Content-Type': entry.type }, body: file });
        if (!res.ok) throw new Error(`upload ${res.status}`);
        entry.url = out.url;
        entry.state = 'done';
      } catch (e) {
        entry.state = 'error';
        this.$bvToast.toast(this.$t('webmag.comments.notSaved'), { variant: 'danger', solid: true });
      }
    },
    removeFile(f) {
      this.files = this.files.filter((x) => x !== f);
    },
    submit() {
      const attachments = this.files.filter((f) => f.state === 'done').map((f) => ({
        url: f.url, name: f.name, type: f.type, size: f.size,
      }));
      this.$emit('submit', { body: this.text.trim(), mentions: mentionIds(this.text, this.picked), attachments }, (ok) => {
        if (!ok) return;
        this.text = '';
        this.files = [];
        this.picked = [];
      });
    },
  },
};
</script>

<style scoped>
/* The panel's look; its scoped styles do not reach into this component. */
.comments-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  resize: vertical;
  font: inherit;
}

.comments-send {
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: #1f2937;
  color: #fff;
  font-weight: 500;
  white-space: nowrap;
}

.comments-send:disabled {
  opacity: 0.4;
}

.comments-link {
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  color: #6b7280;
}

.composer {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  min-width: 0;
}

.composer-field {
  position: relative;
  display: flex;
}

.composer-mentions {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 5;
  min-width: 200px;
  margin: 4px 0 0;
  padding: 4px;
  list-style: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.composer-mentions li {
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}

.composer-mentions li.active,
.composer-mentions li:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.composer-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.composer-file {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 12px;
  background: #f3f4f6;
  font-size: 12px;
}

.composer-file-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.composer-file-state.error {
  color: #b91c1c;
  font-weight: 700;
}

.composer-row {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.composer-attach {
  cursor: pointer;
}

.composer-attach input {
  display: none;
}
</style>
